import { type AppContext } from "@/types";
import { encodeCurrency } from "@/lib/codec";
import { transformApplePayToken } from "@/lib/apple-pay-token-transform";
import { logError, logInfo } from "@/services/logging/logger";

export const APPLE_PAY_VERSION = 5;

export enum ApplePayResultType {
  OK,
  FAILED,
  CANCELED,
}

export type ApplePayResult = {
  type: ApplePayResultType;
  token: string;
  // TODO: Find out how to add TS types for Apple Pay SDK
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  session: any;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValidateMerchantFunc = (validationUrl: string) => Promise<any>;

const formatAmount = (amount: number, precision = 2) => {
  try {
    return parseFloat(`${amount}`).toFixed(precision);
  } catch (err) {
    logError(
      "apple-pay",
      "Error formatting amount",
      err instanceof Error ? err : new Error(String(err)),
      { error: err, amount, precision },
    );
    return amount;
  }
};

export const pay = (
  window: Window,
  ctx: AppContext,
  amount: number,
  validateMerchant: ValidateMerchantFunc,
): Promise<ApplePayResult> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).ApplePaySession === undefined) {
    throw new Error("Apple Pay not available!");
  }

  const payload = {
    countryCode: ctx.session.order.countryCode,
    currencyCode: encodeCurrency(ctx.session.order.currency),
    // since the values of `supportedNetworks` are strings, make sure we use correct one by look at this link
    // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/1916122-supportednetworks
    supportedNetworks: ["visa", "masterCard", "mada", "amex"],
    merchantCapabilities: ["supports3DS"],
    total: {
      label: "Tamara",
      amount: formatAmount(amount),
    },
  };

  logInfo("apple-pay", "Apple Pay Session payload", payload);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const applePaySession = new (window as any).ApplePaySession(
    APPLE_PAY_VERSION,
    payload,
  );

  return new Promise((resolve) => {
    // Track if the session has been cancelled to prevent calling
    // completeMerchantValidation on an invalid session
    let isCancelled = false;

    applePaySession.onvalidatemerchant = onValidateMerchant(
      applePaySession,
      validateMerchant,
      () => isCancelled, // Pass a getter function to check cancellation status
    );

    applePaySession.onpaymentauthorized = onPaymentAuthorized(
      resolve,
      applePaySession,
    );

    applePaySession.oncancel = () => {
      logInfo("apple-pay", "Apple Pay: Payment has been cancelled");
      isCancelled = true;

      resolve({
        type: ApplePayResultType.CANCELED,
        token: "",
        session: applePaySession,
      });
    };

    applePaySession.begin();
  });
};

const onPaymentAuthorized =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (resolve: (result: ApplePayResult) => void, session: any) => (event: any) => {
    let eventEncoded = "";
    try {
      eventEncoded = JSON.stringify(event);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_err) {
      eventEncoded = "";
    }

    logInfo("apple-pay", "Apple Pay: Payment authorized event received", {
      hasEvent: !!event,
      eventEncodedLength: eventEncoded.length,
    });

    const rawToken = event?.payment?.token ?? "";

    let transformedToken = "";
    try {
      transformedToken = transformApplePayToken(rawToken);
    } catch (err) {
      logError(
        "apple-pay",
        "Failed to transform Apple Pay token",
        err instanceof Error ? err : new Error(String(err)),
        { error: err },
      );
      resolve({ type: ApplePayResultType.FAILED, token: "", session });
      return;
    }

    resolve({ type: ApplePayResultType.OK, token: transformedToken, session });
  };

const onValidateMerchant =
  (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    applePaySession: any,
    validateMerchant: ValidateMerchantFunc,
    isCancelled: () => boolean,
  ) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (event: any) => {
    logInfo("apple-pay", "Apple Pay: onValidateMerchant event received");

    const validationURL = event?.validationURL ?? "";

    logInfo("apple-pay", "Apple Pay: Validating merchant", {
      validationURL,
    });

    try {
      const merchantSession = await validateMerchant(validationURL);

      // Check if session was cancelled during async validation
      if (isCancelled()) {
        logInfo(
          "apple-pay",
          "Apple Pay: Session was cancelled during merchant validation, skipping completeMerchantValidation",
        );
        return;
      }

      logInfo("apple-pay", "Apple Pay: Completing merchant validation");

      try {
        applePaySession.completeMerchantValidation(merchantSession);
      } catch (completeError) {
        // Handle case where session might be invalid even if not explicitly cancelled
        logError(
          "apple-pay",
          "Apple Pay: Failed to complete merchant validation (session may be invalid)",
          completeError instanceof Error
            ? completeError
            : new Error(String(completeError)),
          {
            validationURL,
            error: completeError,
          },
        );
      }
    } catch (error) {
      logError(
        "apple-pay",
        "Apple Pay: Failed to validate merchant",
        error instanceof Error ? error : new Error(String(error)),
        {
          validationURL,
          error,
        },
      );

      // Check if session was cancelled during async validation
      if (isCancelled()) {
        logInfo(
          "apple-pay",
          "Apple Pay: Session was cancelled during merchant validation error, skipping completeMerchantValidation",
        );
        return;
      }

      // Complete validation with failure status
      // Apple Pay API requires calling completeMerchantValidation even on error
      // Use an empty object as fallback - Apple Pay will handle the error
      try {
        applePaySession.completeMerchantValidation({});
      } catch (completeError) {
        logError(
          "apple-pay",
          "Apple Pay: Failed to complete merchant validation after error (session may be invalid)",
          completeError instanceof Error
            ? completeError
            : new Error(String(completeError)),
          {
            validationURL,
            error: completeError,
          },
        );
      }
    }
  };

export const completePayment = (
  window: Window,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  applePaySession: any,
  ok: boolean,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const appleStatus = (window as any).ApplePaySession[
    ok ? "STATUS_SUCCESS" : "STATUS_FAILURE"
  ];

  if (appleStatus === undefined)
    throw new Error(`Failed to resolve Apple Pay Status for value ${ok}`);

  applePaySession.completePayment(appleStatus);
};
